/** Embeds HazardHub payload safely inside a standalone HTML script tag. */
export function serializeHazardHubPayload(data: unknown): string {
  return JSON.stringify(data).replace(/</g, '\\u003c');
}

export const HAZARD_HUB_JSON_VIEWER_STYLES = `
  .hh-tabs { display: flex; gap: 4px; margin: 24px 0 0; border-bottom: 1px solid #d2d4d5; }
  .hh-tab {
    padding: 10px 20px;
    border: none;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    background: transparent;
    color: #7b8ead;
    font-family: Inter, Lato, system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: color 0.15s, border-color 0.15s;
  }
  .hh-tab:hover { color: #265dc3; }
  .hh-tab.active { color: #265dc3; border-bottom-color: #265dc3; }
  .hh-tab-panel { display: none; padding-top: 24px; }
  .hh-tab-panel.active { display: block; }
  .json-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }
  .json-search {
    flex: 1;
    min-width: 200px;
    padding: 10px 16px;
    border: 1px solid #d2d4d5;
    border-radius: 8px;
    background: #fff;
    color: #2f353a;
    font-size: 14px;
    font-family: Inter, Lato, system-ui, sans-serif;
  }
  .json-search::placeholder { color: #6d7275; }
  .json-search:focus { outline: none; border-color: #265dc3; box-shadow: 0 0 0 2px rgba(38, 93, 195, 0.15); }
  .json-toolbar-btn {
    padding: 8px 14px;
    border: 1px solid #193f85;
    border-radius: 8px;
    background: #fff;
    color: #2f353a;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
  }
  .json-toolbar-btn:hover { background: #eef3fb; }
  .json-toolbar-btn--primary { border-color: #265dc3; background: #265dc3; color: #fff; }
  .json-toolbar-btn--primary:hover { background: #1e4fa8; border-color: #1e4fa8; }
  .json-copy-toast {
    font-size: 13px;
    color: #1e7a45;
    font-weight: 600;
    opacity: 0;
    transition: opacity 0.2s;
  }
  .json-copy-toast.show { opacity: 1; }
  .json-viewer {
    background: #f0f3f8;
    border: 1px solid #d2d4d5;
    border-radius: 8px;
    padding: 16px;
    overflow: auto;
    max-height: calc(100vh - 220px);
    font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
    font-size: 13px;
    line-height: 1.6;
  }
  .jv-node { margin-left: 0; }
  .jv-children { margin-left: 20px; border-left: 1px solid #d2d4d5; padding-left: 12px; }
  .jv-children.collapsed { display: none; }
  .jv-row { display: flex; align-items: flex-start; gap: 6px; padding: 2px 0; border-radius: 4px; }
  .jv-row:hover { background: rgba(38, 93, 195, 0.06); }
  .jv-row.match {
    background: #fff3cd;
    box-shadow: inset 0 0 0 1px #c9a227;
  }
  .jv-row.match-current {
    background: #ffe08a;
    box-shadow: inset 0 0 0 2px #265dc3;
  }
  .jv-row.match .jv-key,
  .jv-row.match .jv-string,
  .jv-row.match .jv-number,
  .jv-row.match .jv-boolean {
    background: #ffe08a;
    border-radius: 2px;
    padding: 0 2px;
  }
  .jv-toggle {
    width: 16px;
    flex-shrink: 0;
    color: #6d7275;
    cursor: pointer;
    user-select: none;
    font-size: 11px;
    line-height: 1.6;
  }
  .jv-toggle.empty { visibility: hidden; }
  .jv-key { color: #193f85; font-weight: 600; }
  .jv-key::after { content: ':'; color: #6d7275; margin-right: 6px; font-weight: 400; }
  .jv-string { color: #1e7a45; }
  .jv-number { color: #c45c26; }
  .jv-boolean { color: #265dc3; }
  .jv-null { color: #6d7275; font-style: italic; }
  .jv-bracket { color: #6d7275; }
  .jv-preview { color: #7b8ead; font-style: italic; margin-left: 4px; }
  .jv-leaf { padding-left: 22px; }
`;

export const HAZARD_HUB_JSON_VIEWER_SCRIPT = `
(function () {
  var data;
  try {
    data = JSON.parse(document.getElementById('hh-json-data').textContent);
  } catch (e) {
    document.getElementById('json-root').textContent = 'Failed to parse HazardHub JSON.';
    return;
  }

  var root = document.getElementById('json-root');
  var searchInput = document.getElementById('json-search');
  var copyToast = document.getElementById('json-copy-toast');
  var rawJson = JSON.stringify(data, null, 2);

  function esc(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function valueHtml(val) {
    if (val === null) return '<span class="jv-null">null</span>';
    if (typeof val === 'boolean') return '<span class="jv-boolean">' + val + '</span>';
    if (typeof val === 'number') return '<span class="jv-number">' + val + '</span>';
    if (typeof val === 'string') return '<span class="jv-string">"' + esc(val) + '"</span>';
    return '';
  }

  function preview(val) {
    if (Array.isArray(val)) return 'Array(' + val.length + ')';
    if (val && typeof val === 'object') return 'Object(' + Object.keys(val).length + ')';
    return '';
  }

  function renderNode(key, val, path, depth) {
    var isObj = val !== null && typeof val === 'object';
    var isArray = Array.isArray(val);
    var hasChildren = isObj && (isArray ? val.length > 0 : Object.keys(val).length > 0);
    var node = document.createElement('div');
    node.className = 'jv-node';
    node.dataset.path = path;

    if (!isObj) {
      var leaf = document.createElement('div');
      leaf.className = 'jv-row jv-leaf';
      if (key !== null) leaf.innerHTML = '<span class="jv-key">' + esc(key) + '</span>' + valueHtml(val);
      else leaf.innerHTML = valueHtml(val);
      node.appendChild(leaf);
      return node;
    }

    var row = document.createElement('div');
    row.className = 'jv-row';
    var toggle = document.createElement('span');
    toggle.className = 'jv-toggle' + (hasChildren ? '' : ' empty');
    toggle.textContent = hasChildren ? '▼' : '';
    row.appendChild(toggle);

    if (key !== null) {
      var keyEl = document.createElement('span');
      keyEl.className = 'jv-key';
      keyEl.textContent = key;
      row.appendChild(keyEl);
    }

    var bracketOpen = document.createElement('span');
    bracketOpen.className = 'jv-bracket';
    bracketOpen.textContent = isArray ? '[' : '{';
    row.appendChild(bracketOpen);

    if (hasChildren) {
      var prev = document.createElement('span');
      prev.className = 'jv-preview';
      prev.textContent = preview(val);
      row.appendChild(prev);
    }

    node.appendChild(row);

    if (!hasChildren) {
      var closeRow = document.createElement('div');
      closeRow.className = 'jv-leaf';
      closeRow.innerHTML = '<span class="jv-bracket">' + (isArray ? ']' : '}') + '</span>';
      node.appendChild(closeRow);
      return node;
    }

    var children = document.createElement('div');
    children.className = 'jv-children';
    var entries = isArray
      ? val.map(function (v, i) { return [String(i), v]; })
      : Object.keys(val).map(function (k) { return [k, val[k]]; });

    entries.forEach(function (entry) {
      children.appendChild(renderNode(entry[0], entry[1], path + '.' + entry[0], depth + 1));
    });
    node.appendChild(children);

    var closeRow = document.createElement('div');
    closeRow.className = 'jv-leaf';
    closeRow.innerHTML = '<span class="jv-bracket">' + (isArray ? ']' : '}') + '</span>';
    node.appendChild(closeRow);

    if (hasChildren) {
      toggle.addEventListener('click', function () {
        var collapsed = children.classList.toggle('collapsed');
        toggle.textContent = collapsed ? '▶' : '▼';
      });
    }

    return node;
  }

  root.appendChild(renderNode(null, data, 'root', 0));

  function setExpanded(expanded) {
    root.querySelectorAll('.jv-children').forEach(function (el) {
      el.classList.toggle('collapsed', !expanded);
    });
    root.querySelectorAll('.jv-toggle:not(.empty)').forEach(function (el) {
      el.textContent = expanded ? '▼' : '▶';
    });
  }

  document.getElementById('json-expand-all').addEventListener('click', function () { setExpanded(true); });
  document.getElementById('json-collapse-all').addEventListener('click', function () { setExpanded(false); });

  document.getElementById('json-copy').addEventListener('click', function () {
    navigator.clipboard.writeText(rawJson).then(function () {
      copyToast.classList.add('show');
      setTimeout(function () { copyToast.classList.remove('show'); }, 2000);
    });
  });

  var matchRows = [];
  var matchIndex = -1;
  var lastQuery = '';

  function expandAncestors(row) {
    var parent = row.closest('.jv-node');
    while (parent) {
      var ch = parent.querySelector(':scope > .jv-children');
      var tg = parent.querySelector(':scope > .jv-row > .jv-toggle');
      if (ch) ch.classList.remove('collapsed');
      if (tg && !tg.classList.contains('empty')) tg.textContent = '▼';
      parent = parent.parentElement ? parent.parentElement.closest('.jv-node') : null;
    }
  }

  function clearMatchHighlights() {
    root.querySelectorAll('.jv-row.match, .jv-row.match-current').forEach(function (row) {
      row.classList.remove('match');
      row.classList.remove('match-current');
    });
  }

  function collectMatches(query) {
    var q = query.trim().toLowerCase();
    var seen = new Set();
    var matches = [];
    if (!q) return matches;

    root.querySelectorAll('.jv-key, .jv-string, .jv-number, .jv-boolean').forEach(function (el) {
      if (el.textContent.toLowerCase().indexOf(q) === -1) return;
      var row = el.closest('.jv-row');
      if (!row || seen.has(row)) return;
      seen.add(row);
      matches.push(row);
    });
    return matches;
  }

  function scrollToMatch(index) {
    if (!matchRows.length) return;
    var viewer = document.getElementById('json-root');
    var safeIndex = ((index % matchRows.length) + matchRows.length) % matchRows.length;
    matchIndex = safeIndex;

    root.querySelectorAll('.jv-row.match-current').forEach(function (row) {
      row.classList.remove('match-current');
    });

    var row = matchRows[matchIndex];
    row.classList.add('match-current');
    expandAncestors(row);
    if (viewer) {
      row.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  function runSearch(query, focusIndex) {
    var q = query.trim().toLowerCase();
    lastQuery = q;
    clearMatchHighlights();
    matchRows = collectMatches(query);
    matchIndex = matchRows.length ? 0 : -1;

    matchRows.forEach(function (row) {
      row.classList.add('match');
      expandAncestors(row);
    });

    if (matchRows.length) {
      scrollToMatch(typeof focusIndex === 'number' ? focusIndex : 0);
    }
  }

  searchInput.addEventListener('input', function () {
    runSearch(searchInput.value, 0);
  });

  searchInput.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();

    var q = searchInput.value.trim().toLowerCase();
    if (!q) return;

    if (q !== lastQuery || !matchRows.length) {
      runSearch(searchInput.value, 0);
      return;
    }

    scrollToMatch(matchIndex + 1);
  });

  document.querySelectorAll('.hh-tab').forEach(function (tab) {
    tab.addEventListener('click', function () {
      var id = tab.getAttribute('data-tab');
      document.querySelectorAll('.hh-tab').forEach(function (t) { t.classList.remove('active'); });
      document.querySelectorAll('.hh-tab-panel').forEach(function (p) { p.classList.remove('active'); });
      tab.classList.add('active');
      document.getElementById('tab-' + id).classList.add('active');
    });
  });
})();
`;

export function buildHazardHubJsonTabHtml(serializedPayload: string): string {
  return `
    <div id="tab-json" class="hh-tab-panel">
      <div class="json-toolbar">
        <input id="json-search" class="json-search" type="search" placeholder="Search keys and values…" aria-label="Search JSON" />
        <button type="button" id="json-expand-all" class="json-toolbar-btn">Expand all</button>
        <button type="button" id="json-collapse-all" class="json-toolbar-btn">Collapse all</button>
        <button type="button" id="json-copy" class="json-toolbar-btn json-toolbar-btn--primary">Copy JSON</button>
        <span id="json-copy-toast" class="json-copy-toast">Copied!</span>
      </div>
      <div id="json-root" class="json-viewer" role="tree" aria-label="HazardHub JSON"></div>
    </div>
    <script type="application/json" id="hh-json-data">${serializedPayload}</script>
    <script>${HAZARD_HUB_JSON_VIEWER_SCRIPT}</script>
  `;
}
